import React, { useState, useEffect } from 'react';
import { ethers } from 'ethers';
import WalletConnect from './components/WalletConnect';
import AssignmentForm from './components/AssignmentForm';
import VerificationResult from './components/VerificationResult';
import Navbar from './components/Navbar';
import './App.css';

function App() {
  // ============ STATE ============
  const [account, setAccount] = useState(null);
  const [provider, setProvider] = useState(null);
  const [signer, setSigner] = useState(null);
  const [verificationResult, setVerificationResult] = useState(null);
  const [error, setError] = useState(null);

  // ============ WALLET CONNECTION ============
  
  /**
   * Connect to MetaMask wallet
   */
  const connectWallet = async () => {
    try {
      if (!window.ethereum) {
        alert('Please install MetaMask!');
        return;
      }

      // Request account access
      const accounts = await window.ethereum.request({
        method: 'eth_requestAccounts'
      });

      // Set up provider and signer
      const web3Provider = new ethers.BrowserProvider(window.ethereum);
      const web3Signer = await web3Provider.getSigner();

      setAccount(accounts[0]);
      setProvider(web3Provider);
      setSigner(web3Signer);
      setError(null);
      
      console.log('Connected to:', accounts[0]);
    } catch (err) {
      console.error('Error connecting wallet:', err);
      setError('Failed to connect wallet');
    }
  };

  /**
   * Disconnect wallet
   */
  const disconnectWallet = () => {
    setAccount(null);
    setProvider(null);
    setSigner(null);
    setVerificationResult(null);
  };

  // ============ VERIFICATION LOGIC ============

  /**
   * Submit assignment for verification
   */
  const handleVerification = async (assignmentText, paymentProof) => {
    try {
      setError(null);
      setVerificationResult(null);

      // Call backend API
      const backendUrl = import.meta.env.VITE_BACKEND_URL || 'http://localhost:8000';
      const response = await fetch(`${backendUrl}/api/verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          assignment_text: assignmentText,
          user_address: account,
          payment_proof: paymentProof || 'mock_payment' // Mock payment for demo
        })
      });

      const data = await response.json();

      // Handle HTTP 402 Payment Required
      if (response.status === 402) {
        setError({
          type: 'payment_required',
          message: data.message,
          payment_address: data.payment_address,
          cost: data.cost
        });
        return;
      }

      // Handle other errors
      if (!response.ok) {
        throw new Error(data.message || 'Verification failed');
      }

      // Success - display result
      setVerificationResult(data);
      
    } catch (err) {
      console.error('Verification error:', err);
      setError({
        type: 'error',
        message: err.message || 'An error occurred during verification'
      });
    }
  };

  // ============ ACCOUNT CHANGE LISTENER ============

  useEffect(() => {
    if (window.ethereum) {
      window.ethereum.on('accountsChanged', (accounts) => {
        if (accounts.length === 0) {
          disconnectWallet();
        } else {
          setAccount(accounts[0]);
        }
      });
    }

    return () => {
      if (window.ethereum) {
        window.ethereum.removeAllListeners('accountsChanged');
      }
    };
  }, []);

  // ============ RENDER ============

  return (
    <div className="app">
      <Navbar 
        account={account} 
        onConnect={connectWallet}
        onDisconnect={disconnectWallet}
      />

      <main className="container">
        <header className="header">
          <h1>AI Assignment Verifier</h1>
          <p className="subtitle">
            Pay-per-call verification powered by x402 protocol on Monad
          </p>
        </header>

        {/* Wallet Connection Section */}
        {!account ? (
          <WalletConnect onConnect={connectWallet} />
        ) : (
          <>
            {/* Assignment Input Form */}
            <AssignmentForm 
              onSubmit={handleVerification}
              isConnected={!!account}
            />

            {/* Error Display */}
            {error && (
              <div className={`error-box ${error.type === 'payment_required' ? 'payment-required' : ''}`}>
                <h3>
                  {error.type === 'payment_required' ? '💳 Payment Required' : '❌ Error'}
                </h3>
                <p>{error.message}</p>
                {error.payment_address && (
                  <div className="payment-info">
                    <p><strong>Payment Address:</strong> {error.payment_address}</p>
                    <p><strong>Cost:</strong> {error.cost}</p>
                  </div>
                )}
              </div>
            )}

            {/* Verification Result Display */}
            {verificationResult && (
              <VerificationResult result={verificationResult} />
            )}
          </>
        )}

        {/* Info Footer */}
        <footer className="info-footer">
          <p>
            🔒 Tamper-proof verification stored on Monad blockchain
          </p>
          <p>
            🤖 Powered by AI verification engine
          </p>
        </footer>
      </main>
    </div>
  );
}

export default App;
